import React, { useMemo } from "react";
import { useQuery } from '@apollo/client/react';
import '../Settings.scss';
import * as productConfig from '../../../config/productsDictionary.js';
import { PRODUCT_COUNTS } from '../../../services/queries.js';
import DataTable from './DataTable.jsx';

const COLS = [
    { label: 'Product', key: 'product' },
    { label: 'Sector',  key: 'sector',  fmt: v => v || '—' },
    { label: 'Count',   key: 'count',   fmt: v => typeof v === 'number' ? v.toLocaleString() : v },
];

const ProductCounts = () => {
    const PRODUCTS = productConfig.getProducts();

    const { data, loading, error } = useQuery(PRODUCT_COUNTS, {
        fetchPolicy: 'cache-and-network',
    });

    const productArray = useMemo(() => {
        return PRODUCTS ? Object.values(PRODUCTS) : [];
    }, [PRODUCTS]);

    const productArrayWithCounts = useMemo(() => {
        if (loading) {
            return productArray.map(product => ({ ...product, count: 'Loading...' }));
        }

        const countsData = data?.countByProductSector || {};

        return productArray.map(product => {
            const count = countsData.find(item =>
                item.product === product.product &&
                (item.sector || '') === (product.sector || '')
            )?.count ?? 0;
            return { ...product, count };
        });
    }, [productArray, data, loading]);

    const orphanProductCounts = useMemo(() => {
        if (!data?.countByProductSector) return [];

        const validKeys = new Set(
            productArray.map(p => `${p.product}|${p.sector}`)
        );

        const orphans = [];
        const countsData = data.countByProductSector;

        countsData.forEach((sectorObj) => {
            if (!sectorObj || typeof sectorObj !== "object") return;

            Object.entries(sectorObj).forEach(([product, sector, count]) => {
                if (!count) return;

                if (!validKeys.has(`${product}|${sector}`)) {
                    orphans.push({ product, sector, count });
                }
            });
        });

        return orphans;
    }, [data, productArray]);

    return (
        <div className="settings-section">
            <h3>Product Counts</h3>

            <p>All product counts match known products and sectors.
            </p>

            {loading && <div className="settings-loading">Loading product counts...</div>}
            {error && <div className="error">Error loading product counts: {error.message}</div>}

            {PRODUCTS && (
                <DataTable cols={COLS} rows={productArrayWithCounts} />
            )}

            {data && orphanProductCounts.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                    <h4>Orphan Product Counts (no matching product/sector)</h4>
                    <DataTable cols={COLS} rows={orphanProductCounts} />
                </div>
            )}
        </div>
    );
};

export default ProductCounts;
